import myAxios from "./axiositeachWrapper";
import Config from "../_config/config";

/**
 * LessonPlanManagementService
 *
 * Read-only service backing the Lesson Plan Management admin page — a lightweight
 * iteach port of the legacy Java `lessonPlan.do` (LessonPlanAction) search+view flow.
 * It only issues GETs against existing nodefork endpoints (no writes / no new backend):
 *   - list:   GET {nodeSiteUrl}v1/common/lesson-plans/active   (get-active-lesson-plans.controller)
 *   - detail: GET {nodeSiteUrl}v1/study-resources/lesson-plans/:id (get-lesson-plan-by-id.controller)
 *
 * Type IDs (per project rule): 39 = Lesson Plan, 29 = Learning Module.
 */

export const LESSON_PLAN_TYPE = {
  LESSON_PLAN: 39,
  LEARNING_MODULE: 29,
} as const;

export interface LessonPlanListItem {
  id: number;
  name: string;
  code: string | null;
  status: number | null;
  lessonPlanTypeId: number | null;
  lessonPlanType?: "lesson_plan" | "learning_module";
  numberOfClasses: number | null;
  sortOrder: number | null;
  remarks: string | null;
  info: any;
  analyticsDashboard?: number | null;
  creationDate: string | null;
  lastModifiedDate: string | null;
}

export interface LessonPlanStep {
  id: number;
  name: string;
  group?: string;
  sortOrder?: number;
  startDate?: string | null;
  endDate?: string | null;
  resources?: Array<{
    id: number;
    name: string;
    resourceType?: string;
    studyResourceTypeName?: string;
    status?: string;
  }>;
}

export interface LessonPlanGroup {
  index: number;
  name: string;
  color?: string;
  steps: LessonPlanStep[];
}

export interface LessonPlanDetail {
  id: number;
  name: string;
  code: string | null;
  status?: number;
  remarks?: string;
  lessonPlanType?: "lesson_plan" | "learning_module";
  icon?: string | null;
  groups: LessonPlanGroup[];
  selectedTopics: number[];
  selectedBatches: number[];
  creationDate?: string | null;
  lastModifiedDate?: string | null;
}

class LessonPlanManagementService {
  /**
   * List lesson plans / learning modules. The nodefork endpoint returns a raw
   * array; search / type-filter / pagination are applied client-side on the page.
   * Pass `status` to limit to active (1) rows if desired.
   */
  async getLessonPlans(params?: {
    status?: number;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }): Promise<LessonPlanListItem[]> {
    const url = `${Config.nodeSiteUrl}v1/common/lesson-plans/active`;
    const response = await myAxios.get(url, { params });
    // Endpoint returns a bare array; be defensive if it's ever wrapped in { data }.
    const data: any = response.data;
    return Array.isArray(data) ? data : (data?.data ?? []);
  }

  /** Fetch a single lesson plan's detail (groups → steps → resources, topic/batch mappings). */
  async getLessonPlan(id: number): Promise<LessonPlanDetail> {
    const url = `${Config.nodeSiteUrl}v1/study-resources/lesson-plans/${id}`;
    const response = await myAxios.get(url);
    return response.data;
  }
}

export default new LessonPlanManagementService();
